import { AppField, File, FileFieldValue, LookupFieldValue, SimpleRecord } from "@smartsuite/types";
import { getValueFromRecordByField } from "@smartsuite/fields-logic/lib/utils/fields";

/**
 * Retrieves the files associated with a given cover image field from a record.
 *
 * This function processes the provided cover image field to determine its type
 * and retrieves the associated files from the record based on the field's characteristics.
 * It supports file fields and lookup fields with file targets.
 *
 * If the cover image field is undefined or is not recognized as a file field
 * or lookup field with a file target, an empty array is returned.
 *
 */
export const getCoverImageFieldFilesFromRecord = (
  coverImageField: AppField | undefined,
  record: SimpleRecord
): File[] => {
  if (!coverImageField) return [];

  if (isFileField(coverImageField)) return getFileFieldFiles(coverImageField, record);
  if (isLookupWithFileTarget(coverImageField)) return getLookupFieldFiles(coverImageField, record);

  return [];
};

export const isCoverImageSupportedField = (field: AppField): boolean => {
  return isFileField(field) || isLookupWithFileTarget(field);
};

const isFileField = (field: AppField): boolean => {
  return field.field_type === "filefield";
};

const isLookupWithFileTarget = (field: AppField): boolean => {
  if (field.field_type !== "lookupfield") return false;
  return field.params?.target_field_structure?.field_type === "filefield";
};

const getFileFieldFiles = (fileField: AppField, record: SimpleRecord): File[] => {
  return getValueFromRecordByField<FileFieldValue>(record, fileField) || [];
};

const getLookupFieldFiles = (fileField: AppField, record: SimpleRecord): File[] => {
  const value = getValueFromRecordByField<LookupFieldValue<FileFieldValue>>(record, fileField);
  return value ? value.flat(3) : [];
};
